import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  AutoAwesome as AutoAwesomeIcon,
  Upload as UploadIcon,
  Create as CreateIcon,
  PlayArrow as PlayArrowIcon,
  Queue as QueueIcon,
  Description as DescriptionIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  PostAdd as PostAddIcon,
  Info as InfoIcon,
  Analytics as AnalyticsIcon,
  Hub as HubIcon,
  Business as BusinessIcon,
  Policy as PolicyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  BarChart as BarChartIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const DRAWER_WIDTH = 240;

interface MenuItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  description?: string;
}

const menuItems: MenuItem[] = [
  {
    name: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
  },
  {
    name: 'IntelliScript',
    icon: <CodeIcon />,
    children: [
      {
        name: 'IntelliJMX',
        icon: <AutoAwesomeIcon />,
        path: '/intelliscript/ai-har-to-jmeter',
      },
      {
        name: 'PostMan To JMX',
        icon: <PostAddIcon />,
        path: '/intelliscript/postman-to-jmx',
      },
      {
        name: 'Swagger To JMX',
        icon: <CodeIcon />,
        path: '/intelliscript/swagger-to-jmx',
      },
      {
        name: 'Create Custom Script',
        icon: <CreateIcon />,
        path: '/intelliscript/create-custom',
      },
    ],
  },
  {
    name: 'Intelli-LLM',
    icon: <HubIcon />,
    children: [
      {
        name: 'Overview',
        icon: <InfoIcon />,
        path: '/llm-gateway/overview',
        description: 'Intelli-LLM Gateway status and quick links',
      },
      {
        name: 'Tenants',
        icon: <BusinessIcon />,
        path: '/llm-gateway/tenants',
        description: 'Tenant configuration, keys, and integration',
      },
      {
        name: 'Audit log',
        icon: <PolicyIcon />,
        path: '/llm-gateway/audit',
        description: 'Admin change history (when enabled)',
      },
    ],
  },
  {
    name: 'IntelliLoad',
    icon: <SpeedIcon />,
    children: [
      {
        name: 'Benchmarks',
        icon: <AnalyticsIcon />,
        path: '/intelliload/benchmarks',
        description: 'Intelligent performance benchmarks (V2)',
      },
      {
        name: 'Performance Studio',
        icon: <QueueIcon />,
        path: '/intelliload/multi-script',
        description: 'Easily configure and schedule performance tests using one or multiple JMeter scripts.',
      },
    ],
  },
  {
    name: 'IntelliReport',
    icon: <AssessmentIcon />,
    children: [
      {
        name: 'Reports',
        icon: <DescriptionIcon />,
        path: '/intellireport',
        description: 'View past reports and generate new ones',
      },
    ],
  },
];

const superAdminMenuItems: MenuItem[] = [
  {
    name: 'Admin',
    icon: <AdminPanelSettingsIcon />,
    children: [
      {
        name: 'User Usage',
        icon: <BarChartIcon />,
        path: '/admin/user-usage',
        description: 'Per-user statistics across IntelliScript, IntelliLoad, and IntelliReport',
      },
      {
        name: 'Live Infrastructure',
        icon: <CloudIcon />,
        path: '/admin/live-infrastructure',
        description: 'Active tests and AWS instances currently running',
      },
    ],
  },
];

export const Sidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());

  const visibleMenuItems = useMemo(() => {
    if (user?.role === 'super_admin') {
      return [...menuItems, ...superAdminMenuItems];
    }
    return menuItems;
  }, [user?.role]);

  useEffect(() => {
    if (location.pathname.startsWith('/llm-gateway')) {
      setOpenMenus((prev) => new Set(prev).add('Intelli-LLM'));
    }
    if (location.pathname.startsWith('/admin')) {
      setOpenMenus((prev) => new Set(prev).add('Admin'));
    }
  }, [location.pathname]);

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      // Toggle submenu
      const newOpenMenus = new Set(openMenus);
      if (newOpenMenus.has(item.name)) {
        newOpenMenus.delete(item.name);
      } else {
        newOpenMenus.add(item.name);
      }
      setOpenMenus(newOpenMenus);
    } else if (item.path) {
      // Navigate to page
      navigate(item.path);
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const isMenuOpen = (menuName: string) => openMenus.has(menuName);

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = isMenuOpen(item.name);
    const active = isActive(item.path);

    return (
      <Box key={item.name}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleMenuClick(item)}
            selected={active}
            sx={{
              pl: 2 + level * 2,
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: active ? 'primary.contrastText' : 'text.secondary',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 600 : 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    {item.name}
                  </Typography>
                  {item.description && (
                    <Tooltip 
                      title={item.description}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '0.9rem',
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            maxWidth: '300px',
                            lineHeight: '1.4'
                          }
                        }
                      }}
                    >
                      <InfoIcon sx={{ 
                        color: 'primary.main', 
                        fontSize: '1.2rem',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.dark',
                          transform: 'scale(1.1)'
                        }
                      }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
            {hasChildren && (
              <Box sx={{ ml: 'auto' }}>
                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: 0,
          height: '100%',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Navigation
        </Typography>
      </Box>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ py: 1 }}>
          {visibleMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>
    </Drawer>
  );
}; 